import { ApiError, extractError } from '@/api/errors'
import { env } from '@/config/env'
import { getLanguage } from '@/utils/language'
import { FLOW, track } from '@/utils/track'
import { t } from '@/i18n'

export type QueryParams = Record<string, string | number | boolean | null | undefined>

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  params?: QueryParams
  body?: unknown
  signal?: AbortSignal
  /** Таймаут запроса, мс */
  timeout?: number
}

const DEFAULT_TIMEOUT = 20_000

function buildUrl(path: string, params?: QueryParams): string {
  const url = `${env.apiBase}${path.startsWith('/') ? path : `/${path}`}`
  if (!params) return url

  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue
    search.set(key, String(value))
  }
  const query = search.toString()
  return query ? `${url}?${query}` : url
}

/**
 * Единая точка выхода в сеть. Бэкенд умеет отдавать ошибку и HTTP-кодом,
 * и в теле успешного ответа — проверяем оба случая и кидаем ApiError.
 * Тосты показывает `useRequest`, здесь только транспорт.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', params, body, signal, timeout = DEFAULT_TIMEOUT } = options

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeout)
  signal?.addEventListener('abort', () => controller.abort(), { once: true })

  let response: Response
  try {
    response = await fetch(buildUrl(path, params), {
      method,
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        'X-Language': getLanguage(),
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    })
  } catch (error) {
    clearTimeout(timer)
    const isAbort = error instanceof DOMException && error.name === 'AbortError'
    /* Путь без параметров: в них ездят id клиента и телефон */
    track(FLOW.apiFailed, { path, status: 0, reason: isAbort ? 'timeout' : 'offline' })
    throw new ApiError(isAbort ? t('errors.timeout') : t('errors.offline'), 0)
  }
  clearTimeout(timer)

  const raw = await response.text()
  let payload: unknown = raw
  try {
    payload = raw ? JSON.parse(raw) : null
  } catch {
    /* бэкенд иногда отвечает простым текстом — оставляем строкой */
  }

  const message = extractError(payload)

  if (!response.ok) {
    const reason = message ?? response.statusText ?? t('errors.request')
    track(FLOW.apiFailed, { path, status: response.status, reason })
    throw new ApiError(reason, response.status)
  }
  /*
   * Бэкенд умеет отдавать отказ и кодом 200 — такие ошибки до Sentry иначе
   * вообще не доходят, хотя для клиента это тот же обрыв.
   */
  if (message) {
    track(FLOW.apiFailed, { path, status: response.status, reason: message })
    throw new ApiError(message, response.status, true)
  }

  return payload as T
}

export const http = {
  get: <T>(path: string, params?: QueryParams, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET', ...(params ? { params } : {}) }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', ...(body ? { body } : {}) }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', ...(body ? { body } : {}) }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}
